// /src/data/profileService.js
// The single display-name lookup: (id, display_name, avatar_url) for a list of user ids.
//
// Normally this is exactly the get_public_profiles RPC (DATABASE.md § users — the required path for
// author/owner/commenter names, because public.users' own RLS is closed and carries email).
//
// While the demo read target is on (an appointed demo admin viewing /demo/:slug), names come from
// demo.users first — the 23 demo personas exist only there — and any id not found there falls
// back to get_public_profiles. The fallback is required: the demo's groups, articles and some chats
// are owned/authored by a real public.users account (supabase/migrations/20260918c).
// That demo.users read is a ratified EXCEPT to DATABASE.md § users (Architect, 18 Sep 2026): the
// rule protects public.users' closed RLS and email exposure, neither of which applies to the
// admin-only demo schema. Only the three public columns are selected, so the output is identical.
//
// Returns the same { data, error } shape as supabaseFetch.rpc('get_public_profiles', …), so call
// sites convert one-for-one. Brief: docs/briefs/2026-09-18-phase-b-demo-routing-build.md (B1 item 4).

import { supabaseFetch, isDemoReadActive } from '../lib/supabase';

export async function getPublicProfiles(userIds) {
    const ids = [...new Set((userIds || []).filter(Boolean))];
    if (ids.length === 0) return { data: [], error: null };

    if (!isDemoReadActive()) {
        return supabaseFetch.rpc('get_public_profiles', { p_user_ids: ids });
    }

    const { data: personas, error } = await supabaseFetch.query('users', {
        select: 'id,display_name,avatar_url',
        inFilter: { id: ids },
        schema: 'demo'
    });
    if (error) return { data: null, error };

    const found = new Set((personas || []).map(p => p.id));
    const missing = ids.filter(id => !found.has(id));
    if (missing.length === 0) return { data: personas || [], error: null };

    const { data: real, error: realError } = await supabaseFetch.rpc('get_public_profiles', { p_user_ids: missing });
    if (realError) return { data: personas || [], error: realError };
    return { data: [...(personas || []), ...(real || [])], error: null };
}
